import { buildImportAliasMap, normalizeHeader } from '../../data/productionSpeciesPresets'
import { normalizeToIsoZMidnight } from '../../utils/dateNormalization'
import {
  buildImportSession,
  buildImportValidationResults,
  createImportDedupeIssue,
  createImportRowIssue,
  buildImportPreviewRecord,
  buildImportPreviewRows,
  mapImportHeaders,
  type ImportMappedHeader,
  type ImportRecordResult,
  type ImportSession,
  type ImportSourceFile,
  parseCsvImportTable,
  type ParsedImportTable
} from '../../utils/importTable'
import { ensureUniqueImportedRowId, toStableImportIdString } from '../../utils/importRowIdentity'
import { resolveCanonicalLotKey } from '../../utils/lotIdentity'
import { toDayKeyDateOnly } from '../../utils/productionFeedKgDerivation'
import type { BiomassDataRow } from '../../utils/productionUnitTable'
import {
  ensureImportHasAllColumns,
  normalizeImportedMappedValue,
  normalizeImportedRowDate,
  reconcileImportedRowLot
} from './datatableHelpers'

export type ProductionSummaryImportLot = {
  id: string
  lotKey: string
  stage?: string
}

type ProductionSummaryImportTargetKey = 'productionDatatable'
type ProductionSummaryImportContext = {
  farmId: string
  productionUnitId: string
  speciesKey: string
}

export type ProductionSummaryMappedHeader = ImportMappedHeader<string>
export type ProductionSummaryImportRowResult = ImportRecordResult<BiomassDataRow, string>
export type ProductionSummaryImportSession = ImportSession<
  BiomassDataRow,
  string,
  ProductionSummaryImportTargetKey,
  ProductionSummaryImportContext
>

const buildExistingColumnMap = (activeColumns: string[]): Record<string, string> => {
  const existingMap: Record<string, string> = {}
  activeColumns.forEach((column) => {
    existingMap[normalizeHeader(column)] = column
  })
  existingMap.id = 'id'
  existingMap[normalizeHeader('lotId')] = 'lotId'
  existingMap[normalizeHeader('unmappedLot')] = 'unmappedLot'
  return existingMap
}

const ensureImportedRowId = (
  row: Record<string, unknown>,
  existingIds: Set<string>,
  importedIds: Set<string>
) => {
  return ensureUniqueImportedRowId({
    row,
    existingIds,
    importedIds,
    idPrefix: 'prod'
  })
}

const validateProductionSummaryImportRecord = (args: {
  previewRow: Parameters<typeof createImportRowIssue<string>>[0]['previewRow']
  record: Record<string, unknown>
}) => {
  const issues = []
  const rawDate = typeof args.record.date === 'string' ? args.record.date.trim() : ''

  if (!rawDate) {
    issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: 'date',
      code: 'production-date-required',
      severity: 'error',
      message: 'Date is required.'
    }))
    return issues
  }

  const normalizedDate = normalizeToIsoZMidnight(rawDate)
  if (!normalizedDate.ok) {
    issues.push(createImportRowIssue({
      previewRow: args.previewRow,
      targetField: 'date',
      code: 'production-date-invalid',
      severity: 'error',
      message: 'Date must be a recognizable date.'
    }))
  }

  return issues
}

export const getProductionSummaryMissingImportColumns = (args: {
  activeColumns: string[]
  speciesKey: string
}): string[] => {
  const aliasMap = buildImportAliasMap(args.speciesKey)
  const aliasTargets = Array.from(new Set(Object.values(aliasMap)))
  return aliasTargets.filter((target) => target !== 'id' && !args.activeColumns.includes(target))
}

const buildResolvedProductionSummaryColumns = (args: {
  activeColumns: string[]
  speciesKey: string
}): string[] => {
  const missingColumns = getProductionSummaryMissingImportColumns(args)
  if (missingColumns.length === 0) return args.activeColumns.slice()

  const seen = new Set(args.activeColumns)
  return args.activeColumns.concat(missingColumns.filter((column) => !seen.has(column)))
}

export const mapProductionSummaryHeadersToColumns = (args: {
  headers: string[]
  activeColumns: string[]
  speciesKey: string
}): ProductionSummaryMappedHeader[] => {
  const aliasMap = buildImportAliasMap(args.speciesKey)
  const existingMap = buildExistingColumnMap(args.activeColumns)
  return mapImportHeaders({
    headers: args.headers,
    normalizeHeader,
    targetByNormalizedHeader: {
      ...aliasMap,
      ...existingMap
    }
  })
}

export const buildProductionSummaryImportedRowsFromTable = (args: {
  table: ParsedImportTable
  mappedHeaders: ProductionSummaryMappedHeader[]
  activeColumns: string[]
  existingRows: BiomassDataRow[]
  availableLots: ProductionSummaryImportLot[]
  lotKeyById: Map<string, string>
  /** v2.1 #47: farm timezone used to derive `(lot, day)` dedup keys. */
  timeZone?: string
}): BiomassDataRow[] => {
  return buildProductionSummaryImportResultsFromTable(args).map((result) => result.record)
}

export const buildProductionSummaryImportResultsFromTable = (args: {
  table: ParsedImportTable
  mappedHeaders: ProductionSummaryMappedHeader[]
  activeColumns: string[]
  existingRows: BiomassDataRow[]
  availableLots: ProductionSummaryImportLot[]
  lotKeyById: Map<string, string>
  /**
   * v2.1 #47: optional farm (or browser) timezone used to derive the
   * `(lot, day)` dedup key for the `production-duplicate-lot-day` warning.
   * Without it, the legacy UTC-prefix behavior is preserved — which causes
   * late-evening rows in negative-UTC farms to bucket with the next UTC day
   * and falsely report duplicates against the following day's rows. Callers
   * from ImportWorkspace should pass `resolveTimeZone(farmTimeZone)`.
   */
  timeZone?: string
}): ProductionSummaryImportRowResult[] => {
  const existingIds = new Set(args.existingRows.map((row) => toStableImportIdString(row.id)))
  const importedIds = new Set<string>()
  const dayKeyOptions = args.timeZone ? { timeZone: args.timeZone } : undefined
  // #41: preventive (lot, day) dedup. Index existing rows by `(lotKey, dayKey)`
  // — the same key the curative #36 detector uses — so we can warn the user at
  // review time that an imported row will create a duplicate. The import still
  // proceeds (no merge UI yet); the `Fuse duplicates (N)` button surfaces post-
  // import to consolidate.
  // v2.1 #47: when `args.timeZone` is provided, derive the day key in that
  // zone so we match the operator's local calendar rather than UTC.
  const existingLotDayKeys = new Set<string>()
  for (const row of args.existingRows) {
    if (!row || row.archived === '1') continue
    const lotToken = resolveCanonicalLotKey(row.lotId, row.lot)
    if (lotToken === 'unknown-lot') continue
    const dayKey = toDayKeyDateOnly(row.date ?? '', dayKeyOptions)
    if (!dayKey) continue
    existingLotDayKeys.add(`${dayKey}::${lotToken}`)
  }
  const importedLotDayKeys = new Set<string>()
  const previewRows = buildImportPreviewRows({
    table: args.table,
    mappedHeaders: args.mappedHeaders,
    normalizeCellValue: (value) => value.trim()
  })

  return buildImportValidationResults({
    previewRows,
    buildRecord: (previewRow) => buildImportPreviewRecord({
      previewRow,
      mapCellValue: (cell) => normalizeImportedMappedValue(cell.targetField, cell.value)
    }),
    validateRecord: ({ previewRow, record }) => validateProductionSummaryImportRecord({ previewRow, record })
  }).map((validationResult) => {
    const row = validationResult.record
    const dedupeResult = ensureImportedRowId(row, existingIds, importedIds)
    const dedupeIssue = createImportDedupeIssue({
      previewRow: validationResult.previewRow,
      targetField: 'id',
      dedupeResult
    })

    normalizeImportedRowDate(row)
    reconcileImportedRowLot({
      obj: row,
      availableLots: args.availableLots,
      lotKeyById: args.lotKeyById
    })
    ensureImportHasAllColumns(row, args.activeColumns)

    const typedRow: BiomassDataRow = {
      id: toStableImportIdString(row.id),
      date: typeof row.date === 'string' ? row.date : '',
      lot: typeof row.lot === 'string' ? row.lot : '',
      lotId: typeof row.lotId === 'string' ? row.lotId : '',
      unmappedLot: typeof row.unmappedLot === 'string' ? row.unmappedLot : '',
      stage: row.stage === 'Inactive' ? 'Inactive' : 'Active',
      weight: typeof row.weight === 'string' ? row.weight : '',
      dailyMortality: typeof row.dailyMortality === 'string' ? row.dailyMortality : '',
      fishStock: typeof row.fishStock === 'string' ? row.fishStock : '',
      health: typeof row.health === 'string' ? row.health : '',
      healthDetail: typeof row.healthDetail === 'string' ? row.healthDetail : ''
    }

    Object.entries(row).forEach(([key, value]) => {
      if (key in typedRow) return
      if (value === undefined || value === null) {
        typedRow[key] = ''
        return
      }
      if (typeof value === 'string') {
        typedRow[key] = value
      }
    })

    // #41: preventive (lot, day) duplicate warning. Emit a warning when the
    // imported row will collide with an existing row OR with a previously-
    // imported row in this same batch.
    const issues = dedupeIssue ? [...validationResult.issues, dedupeIssue] : [...validationResult.issues]
    const lotToken = resolveCanonicalLotKey(typedRow.lotId, typedRow.lot)
    const dayKey = toDayKeyDateOnly(typedRow.date, dayKeyOptions)
    if (lotToken !== 'unknown-lot' && dayKey) {
      const lotDayKey = `${dayKey}::${lotToken}`
      const collidesWithExisting = existingLotDayKeys.has(lotDayKey)
      const collidesWithImported = importedLotDayKeys.has(lotDayKey)
      if (collidesWithExisting || collidesWithImported) {
        issues.push(createImportRowIssue({
          previewRow: validationResult.previewRow,
          targetField: 'date',
          code: 'production-duplicate-lot-day',
          severity: 'warning',
          message: collidesWithExisting
            ? 'A row for this lot and day already exists in the table. The import will create a duplicate; use the "Fuse duplicates" button afterwards to consolidate.'
            : 'Another imported row already targets this lot and day. The import will create a duplicate; use the "Fuse duplicates" button afterwards to consolidate.'
        }))
      }
      importedLotDayKeys.add(lotDayKey)
    }

    // v2.1 #44: warn the user at preview time when the imported row will be
    // saved local-only (i.e. `isSyncableProductionRow` will reject it for the
    // entity outbox). The most common cause is that `reconcileImportedRowLot`
    // could not match the source lot label against the active lots for the
    // selected production unit — `lot` is cleared and the original label is
    // parked in `unmappedLot`. The row still saves to localStorage and shows
    // up in the table, but it never reaches the API until the operator maps
    // the lot via Database's per-row reconciliation affordance (or creates
    // / activates the lot in this PU). Surfacing this as a `warning` keeps
    // the import non-blocking while explaining the "N row(s) remained local-
    // only and were not queued for sync" tally that lands in the post-import
    // toast.
    const trimmedLotLabel = typedRow.lot.trim()
    if (!trimmedLotLabel) {
      const unmappedLabel = (typedRow.unmappedLot ?? '').trim()
      issues.push(createImportRowIssue({
        previewRow: validationResult.previewRow,
        targetField: 'lot',
        code: 'production-unmapped-lot',
        severity: 'warning',
        message: unmappedLabel
          ? `Lot "${unmappedLabel}" is not active in this production unit. The row will be saved locally but will not sync until the lot is created/activated here or mapped to an existing lot from the Database table.`
          : 'This row has no lot. It will be saved locally but will not sync until a lot is assigned from the Database table.'
      }))
    }

    return {
      ...validationResult,
      record: typedRow,
      issues,
      dedupeResult
    }
  })
}

const buildProductionSummaryImportArtifactsFromCsvText = (args: {
  text: string
  activeColumns: string[]
  speciesKey: string
  existingRows: BiomassDataRow[]
  availableLots: ProductionSummaryImportLot[]
  lotKeyById: Map<string, string>
  timeZone?: string
}): {
  table: ParsedImportTable
  mappedHeaders: ProductionSummaryMappedHeader[]
  activeColumns: string[]
  results: ProductionSummaryImportRowResult[]
} | null => {
  const table = parseCsvImportTable(args.text)
  if (!table) return null

  const activeColumns = buildResolvedProductionSummaryColumns({
    activeColumns: args.activeColumns,
    speciesKey: args.speciesKey
  })
  const mappedHeaders = mapProductionSummaryHeadersToColumns({
    headers: table.headers,
    activeColumns,
    speciesKey: args.speciesKey
  })
  const results = buildProductionSummaryImportResultsFromTable({
    table,
    mappedHeaders,
    activeColumns,
    existingRows: args.existingRows,
    availableLots: args.availableLots,
    lotKeyById: args.lotKeyById,
    timeZone: args.timeZone
  })

  return {
    table,
    mappedHeaders,
    activeColumns,
    results
  }
}

export const buildProductionSummaryImportSessionFromCsvText = (args: {
  text: string
  activeColumns: string[]
  speciesKey: string
  existingRows: BiomassDataRow[]
  availableLots: ProductionSummaryImportLot[]
  lotKeyById: Map<string, string>
  sourceFile?: ImportSourceFile | null
  assignedContext?: {
    farmId: string
    productionUnitId: string
  } | null
  targetLabel?: string
  /** v2.1 #47: farm timezone for `(lot, day)` dedup key derivation. */
  timeZone?: string
}): ProductionSummaryImportSession | null => {
  const artifacts = buildProductionSummaryImportArtifactsFromCsvText(args)
  if (!artifacts) return null

  return buildImportSession({
    sourceFile: args.sourceFile ?? null,
    table: artifacts.table,
    selectedTarget: {
      targetKey: 'productionDatatable',
      label: args.targetLabel ?? 'Production Datatable'
    },
    assignedContext: args.assignedContext
      ? {
          ...args.assignedContext,
          speciesKey: args.speciesKey
        }
      : null,
    mappedHeaders: artifacts.mappedHeaders,
    results: artifacts.results
  })
}